import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { TrashIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { getIdeaComments, getTaskComments, deleteComment, formatCommentContent } from '../../lib/comments';
import { CommentWithProfile } from '../../lib/comments';
import { useApp } from '../../context/SupabaseAppContext';

interface CommentListProps {
  ideaId?: string;
  taskId?: string;
  title?: string;
  className?: string;
  refreshTrigger?: number; // Used to trigger refresh when new comments are added
}

const CommentList: React.FC<CommentListProps> = ({
  ideaId,
  taskId,
  title = "Comments",
  className = "",
  refreshTrigger
}) => {
  const [comments, setComments] = useState<CommentWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const { state } = useApp();

  // Load comments when component mounts or IDs change
  useEffect(() => {
    loadComments();
  }, [ideaId, taskId, refreshTrigger]);



  const loadComments = async () => {
    setLoading(true);
    try {
      let fetchedComments: CommentWithProfile[] = [];
      
      if (ideaId) {
        fetchedComments = await getIdeaComments(ideaId);
      } else if (taskId) {
        fetchedComments = await getTaskComments(taskId);
      }
      
      setComments(fetchedComments);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    setDeletingCommentId(commentId);
    
    try {
      const success = await deleteComment(commentId);
      if (success) {
        setComments(prev => prev.filter(comment => comment.id !== commentId));
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setDeletingCommentId(null);
    }
  };

  const canDeleteComment = (comment: CommentWithProfile): boolean => {
    return state.currentUser?.id === comment.author.id;
  };

  const addComment = (newComment: CommentWithProfile) => {
    setComments(prev => [...prev, newComment]);
  };

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-sm font-semibold text-gray-700">{title}</h5>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-deep-teal"></div>
          <span className="ml-2 text-sm text-gray-600">Loading comments...</span>
        </div>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-sm font-semibold text-gray-700">{title}</h5>
        </div>
        <div className="text-center py-8 text-gray-500">
          <ChatBubbleLeftIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No comments yet</p>
          <p className="text-xs text-gray-400 mt-1">Be the first to share your thoughts!</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-sm font-semibold text-gray-700">
          {title} ({comments.length})
        </h5>
      </div>
      
      <div className="space-y-3">
        {comments.map((comment) => (
          <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center mb-2">
                <span className="text-lg mr-3">{comment.author.avatar}</span>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900 text-sm">{comment.author.name}</span>
                    <span className="text-xs text-gray-500">
                      {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  {comment.updated_at !== comment.created_at && (
                    <span className="text-xs text-gray-400">
                      (edited {format(new Date(comment.updated_at), 'MMM d, h:mm a')})
                    </span>
                  )}
                </div>
              </div>
              
              {canDeleteComment(comment) && (
                <button
                  onClick={() => handleDeleteComment(comment.id)}
                  disabled={deletingCommentId === comment.id}
                  className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                  title="Delete comment"
                >
                  {deletingCommentId === comment.id ? (
                    <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                  ) : (
                    <TrashIcon className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
            
            <div className="mt-2">
              <div 
                className="text-sm text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: formatCommentContent(comment.content) }}
              />
            </div>
            
            {comment.mentions.length > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                <span>Mentions: {comment.mentions.join(', ')}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommentList; 